<template>
  <div class="order-tracking-info">
    <h3 class="section-subheader">
      {{ t('orders.orderNumParam', { param: orderId }) }}
    </h3>

    <InputWrapper
      :input-id="`${id}-emails`"
      :label="
        t('common.shareParam', {
          param: t('orders.trackingInformation'),
        })
      "
      class="emails-container"
      :errors="v$.$errors"
    >
      <template #label="{ inputId, label }">
        <LabeledIcon
          class="input-wrapper-label-container"
          icon="pi pi-user-plus"
        >
          <template #label>
            <label :for="inputId">{{ label }}</label>
          </template>
        </LabeledIcon>
      </template>

      <template #default="{ ariaProps, inputClasses, inputId, invalid }">
        <div class="input-container flex flex-gap-1 align-items-center">
          <InputText
            :id="inputId"
            v-model="sharingRequest.recipients"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :placeholder="t('shipments.emailsCommaSeparated')"
            class="flex-grow-1"
            separator=","
          />

          <div class="input-container-button">
            <RygenButton
              mode="primary"
              :label="t('common.share')"
              :loading="loading"
              @click="onSend"
            />
          </div>
        </div>
      </template>
    </InputWrapper>

    <InputWrapper
      :input-id="`${id}-link`"
      :label="t('shipments.getLink')"
      class="link-container"
    >
      <template #label="{ inputId, label }">
        <LabeledIcon class="input-wrapper-label-container" icon="pi pi-link">
          <template #label>
            <label :for="inputId">{{ label }}</label>
          </template>
        </LabeledIcon>
      </template>

      <template #default="{ ariaProps, inputId }">
        <div class="input-container">
          <InputText
            :id="inputId"
            v-bind="ariaProps"
            :model-value="url"
            class="flex-grow-1"
            disabled
          />

          <div class="input-container-button">
            <RygenCopyButton
              mode="tertiary"
              icon="pi pi-copy"
              icon-pos="left"
              :label="t('common.copyParam', { param: t('common.links') })"
              :copy-value="url"
            />
          </div>
        </div>
      </template>
    </InputWrapper>
  </div>
</template>

<script lang="ts" setup>
import { PropType, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  matchesEmail,
  required,
  useValidations,
} from '@rygen/rygen-ui/composables';

import { SharingRequest } from '@/model';

import LabeledIcon from '@/components/ui/LabeledIcon.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import RygenCopyButton from '@rygen/rygen-ui/components/button/RygenCopyButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import InputText from 'primevue/inputtext';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  orderId: {
    type: Number,
    required: true,
  },
  url: {
    type: String,
    required: true,
  },
  suggestedEmails: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { id, orderId, url, loading, suggestedEmails } = toRefs(props);
const emit = defineEmits<{
  (e: 'share-via-email', sharingRequest: SharingRequest): void;
}>();

const { t } = useI18n();

const rules = {
  recipients: {
    required: required(t('common.email')),
    matches: matchesEmail(),
  },
};

const sharingRequest = ref({ recipients: suggestedEmails.value.join(', ') });

const { v$, validate } = useValidations(sharingRequest, rules);

const onSend = async () => {
  if (await validate()) {
    emit(
      'share-via-email',
      new SharingRequest({
        recipients: sharingRequest.value.recipients.split(','),
      }),
    );
  }
};
</script>

<style>
.order-tracking-info {
  .emails-container {
    padding-bottom: var(--sp-3);

    .p-chips {
      height: var(--input-height);

      .p-chips-multiple-container {
        flex-grow: 1;
        padding: 0.175rem 0.75rem;
      }
    }
  }

  .p-chips.custom-chips .p-chips-multiple-container {
    padding: 0 0.75rem;

    .p-chips-token {
      border-radius: var(--p-border-radius-sm);
      color: var(--info-color);
      border: 1px solid var(--p-gray-300);
      padding: 0.125rem 0.75rem;
      margin-right: 0.5rem;
      background: var(--p-gray-200);
    }

    .p-chips-input-token {
      padding-top: 0;
      padding-bottom: 0;
      max-width: 50px;
    }
  }

  .link-container {
    padding-top: var(--sp-3);
  }

  .input-wrapper-label-container {
    gap: var(--sp-2);
    margin-bottom: var(--sp-3);

    i.pi {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background-color: var(--info-light-color);
      color: var(--info-color);
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
    }

    label {
      font-size: 16px;
      font-weight: 400;
      text-transform: none;
    }
  }

  .input-container {
    display: flex;
    gap: var(--sp-3);
    align-items: center;

    .input-container-button {
      width: 25%;
    }
  }
}
</style>
